import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { 
  Typography, 
  Button, 
  List, 
  ListItem, 
  ListItemText, 
  Divider, 
  IconButton, 
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  MenuItem,
  Menu,
  Select,
  FormControl,
  InputLabel,
  Chip,
  SelectChangeEvent,
  Snackbar,
  Alert,
  Tooltip,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import FolderIcon from '@mui/icons-material/Folder';
import BugReportIcon from '@mui/icons-material/BugReport';
import DescriptionIcon from '@mui/icons-material/Description';
import { artifactService } from '../../services/artifactService';
import { reportService } from '../../services/reportService';
import { Artifact, Finding } from '../../types/api.types';
import RefreshIcon from '@mui/icons-material/Refresh';
import GetAppIcon from '@mui/icons-material/GetApp';
import SortIcon from '@mui/icons-material/Sort';
import FileViewer from '../file/FileViewer';
import { validateFile, getAcceptAttribute, getAllowedFileTypesDisplay } from '../../utils/fileValidation';
import { useFormValidation } from '../../hooks/useInputValidation';

const PanelContainer = styled('div')<{ width: number }>(({ theme, width }) => ({
  width: `${width}px`,
  minWidth: '250px',
  maxWidth: '600px',
  height: '100%',
  borderLeft: `1px solid #e0e0e0`,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#ffffff',
  position: 'relative',
}));

const SectionContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflowY: 'auto',
  padding: theme.spacing(2, 2, 0, 2),
  backgroundColor: '#ffffff',
}));

const SectionHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  paddingBottom: theme.spacing(2),
  marginBottom: theme.spacing(1),
}));

const ItemList = styled(List)(({ theme }) => ({
  padding: 0,
  overflowY: 'auto',
  flex: 1,
  '& .MuiListItem-root': {
    borderRadius: theme.spacing(1),
    margin: theme.spacing(0, 0, 1.5, 0),
    padding: theme.spacing(2),
    border: '1px solid #e0e0e0',
    backgroundColor: '#ffffff',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    '&:hover': {
      backgroundColor: '#f8f9fa',
      border: `1px solid #d0d0d0`,
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
    },
  },
}));

const AddButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#11CAF0',
  color: '#ffffff',
  borderRadius: '20px',
  textTransform: 'none',
  fontWeight: 500,
  padding: theme.spacing(0.75, 2),
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: '#0ea5c7',
  },
  width: '139px',
  height: '37px',
}));

const ResizeHandle = styled('div')(({ theme }) => ({
  position: 'absolute',
  left: -5,
  top: 0,
  height: '100%',
  width: 10,
  cursor: 'ew-resize',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#757575',
  backgroundColor: 'transparent',
  transition: 'all 0.2s ease',
  '&:hover': {
    color: '#1976d2',
    backgroundColor: 'rgba(17, 202, 240, 0.1)',
  },
  '&:active': {
    backgroundColor: 'rgba(17, 202, 240, 0.2)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: 3,
    height: 40,
    backgroundColor: 'currentColor',
    borderRadius: 2,
    opacity: 0.3,
  },
  '&:hover::before': {
    opacity: 0.7,
  },
  zIndex: 1,
}));



interface RightPanelProps {
  artifacts: Artifact[];
  findings: Finding[];
  reportsData?: Finding[]; // Reports array for count display
  onAddArtifact: (name: string, type: string) => void;
  onAddFinding: (name: string) => void;
  newFindingsCount?: number;
  newArtifactsCount?: number;
  onFindingsTabClick?: () => void;
  onArtifactsTabClick?: () => void;
  onRefreshFindings?: () => void; // Function to refresh findings from parent
  onRefreshArtifacts?: () => void; // Function to refresh artifacts from parent
  onRefreshReports?: () => void; // Function to refresh reports from parent
  reportRefreshTrigger?: number; // Trigger to force refresh reports from parent
  report?: any; // We'll need to define a proper type for this later
  projectId: string; // Project ID for uploading files to chat
  onGenerateReport?: (projectId: string) => Promise<boolean>; // Function to generate report from parent component
  width?: number; // Current width of the panel
  onWidthChange?: (width: number) => void; // Callback when width changes
}

const RightPanel: React.FC<RightPanelProps> = ({
  artifacts = [],
  findings = [],
  reportsData = [],
  onAddArtifact,
  onAddFinding,
  newFindingsCount = 0,
  newArtifactsCount = 0,
  onFindingsTabClick,
  onArtifactsTabClick,
  onRefreshFindings,
  onRefreshArtifacts,
  onRefreshReports,
  reportRefreshTrigger,
  report,
  projectId,
  onGenerateReport,
  width = 300,
  onWidthChange,
}) => {
  // State for artifact dialog
  const [artifactDialogOpen, setArtifactDialogOpen] = useState(false);
  const [artifactType, setArtifactType] = useState('scope');
  const [artifactSubmitting, setArtifactSubmitting] = useState(false);
  
  // Artifact form validation
  const artifactFormValidation = useFormValidation({
    artifactName: {
      validationType: 'artifactName',
      initialValue: '',
    },
  });
  
  // State for finding dialog
  const [findingDialogOpen, setFindingDialogOpen] = useState(false);
  const [findingSeverity, setFindingSeverity] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');
  const [findingSubmitting, setFindingSubmitting] = useState(false);
  
  // Finding form validation
  const findingFormValidation = useFormValidation({
    findingName: {
      validationType: 'analysisName',
      initialValue: '',
    },
    findingDescription: {
      validationType: 'analysisDescription',
      initialValue: '',
    },
  });
  
  // State for file upload
  const [uploadingArtifacts, setUploadingArtifacts] = useState<{ [key: string]: boolean }>({});
  const [fileUploadTarget, setFileUploadTarget] = useState<{ id: string, type: 'artifact' | 'finding' } | null>(null);
  
  // State for file viewer
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [fileViewerProps, setFileViewerProps] = useState<{
    fileUrl: string;
    fileName: string;
    fileType: string;
  } | null>(null);
  
  // File input ref
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // State for error messages
  const [error, setError] = useState<string | null>(null);
  
  // State for current tab
  const [currentTab, setCurrentTab] = useState<number>(0);
  
  // State for resizing
  const [isResizing, setIsResizing] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(width);
  
  // State for tracking scan activity
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [lastArtifactCount, setLastArtifactCount] = useState<number>(0);
  const [lastFindingCount, setLastFindingCount] = useState<number>(0);
  const [scanDetectionTimer, setScanDetectionTimer] = useState<NodeJS.Timeout | null>(null);
  
  // State for sorting
  const [artifactSortOrder, setArtifactSortOrder] = useState<'desc' | 'asc'>('desc'); // Default to newest first
  const [findingSortOrder, setFindingSortOrder] = useState<'desc' | 'asc'>('desc'); // Default to newest first
  const [reportSortOrder, setReportSortOrder] = useState<'desc' | 'asc'>('desc'); // Default to newest first
  
  // State for download menu
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedReportForDownload, setSelectedReportForDownload] = useState<string | null>(null);

  // Detect scan activity by monitoring artifact changes
  useEffect(() => {
    if (artifacts.length > lastArtifactCount) {
      console.log('RightPanel: New artifacts detected, scan may be active');
      setIsScanning(true);
      setLastArtifactCount(artifacts.length);
      
      // Clear existing timer
      if (scanDetectionTimer) {
        clearTimeout(scanDetectionTimer);
      }
      
      // Set timer to stop scan detection after 2 minutes of no new artifacts
      const timer = setTimeout(() => {
        console.log('RightPanel: No new artifacts for 2 minutes, assuming scan complete');
        setIsScanning(false);
      }, 120000); // 2 minutes
      
      setScanDetectionTimer(timer);
    }
  }, [artifacts.length, lastArtifactCount, scanDetectionTimer]);

  // Monitor findings count for scan completion
  useEffect(() => {
    if (findings.length > lastFindingCount) {
      console.log('RightPanel: New findings detected, analysis may be completing');
      setLastFindingCount(findings.length);
    }
  }, [findings.length, lastFindingCount]);

  // Adaptive polling for findings - more frequent during scans
  useEffect(() => {
    let refreshInterval: NodeJS.Timeout | null = null;
    
    if (projectId && onRefreshFindings) {
      // Use faster polling during active scans for findings (analysis results)
      const interval = isScanning ? 5000 : 15000; // 5 seconds during scans, 15 seconds normally
      
      console.log(`RightPanel: Setting up findings polling - interval: ${interval}ms, scanning: ${isScanning}`);
      
      refreshInterval = setInterval(() => {
        console.log(`Auto-refreshing findings (scanning: ${isScanning})...`);
        onRefreshFindings();
      }, interval);
    }
    
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [projectId, onRefreshFindings, isScanning]);
  
  // Adaptive polling for artifacts - more frequent during scans
  useEffect(() => {
    let refreshInterval: NodeJS.Timeout | null = null;
    
    if (projectId && onRefreshArtifacts) {
      // Use faster polling during active scans for artifacts (scan outputs)
      const interval = isScanning ? 3000 : 12000; // 3 seconds during scans, 12 seconds normally
      
      console.log(`RightPanel: Setting up artifacts polling - interval: ${interval}ms, scanning: ${isScanning}`);
      
      refreshInterval = setInterval(() => {
        console.log(`Auto-refreshing artifacts (scanning: ${isScanning})...`);
        onRefreshArtifacts();
      }, interval);
    }
    
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [projectId, onRefreshArtifacts, isScanning]);

  // Adaptive polling for reports - more frequent during scans and report generation
  useEffect(() => {
    let refreshInterval: NodeJS.Timeout | null = null;
    
    if (projectId && onRefreshReports) {
      // Use adaptive polling for reports - faster during scans, regular otherwise
      const interval = isScanning ? 5000 : 10000; // 5 seconds during scans, 10 seconds normally
      
      console.log(`RightPanel: Setting up reports polling - interval: ${interval}ms, scanning: ${isScanning}`);
      
      refreshInterval = setInterval(async () => {
        console.log(`Auto-refreshing reports (scanning: ${isScanning})...`);
        await onRefreshReports();
        // Trigger content check after every refresh
        setContentCheckTrigger(prev => prev + 1);
      }, interval);
    }
    
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [projectId, onRefreshReports, isScanning]);

  // Cleanup scan detection timer on unmount
  useEffect(() => {
    return () => {
      if (scanDetectionTimer) {
        clearTimeout(scanDetectionTimer);
      }
    };
  }, [scanDetectionTimer]);

  // Additional aggressive polling when Reports tab is active
  useEffect(() => {
    let aggressiveRefreshInterval: NodeJS.Timeout | null = null;
    
    if (currentTab === 2 && projectId && onRefreshReports) { // Tab 2 is Reports
      console.log('RightPanel: Reports tab active, starting aggressive polling every 5 seconds');
      
      aggressiveRefreshInterval = setInterval(async () => {
        console.log('Aggressive reports refresh (Reports tab active)...');
        await onRefreshReports();
        // Trigger content check after refresh
        setContentCheckTrigger(prev => prev + 1);
      }, 5000); // Every 5 seconds when Reports tab is active
    }
    
    return () => {
      if (aggressiveRefreshInterval) {
        clearInterval(aggressiveRefreshInterval);
      }
    };
  }, [currentTab, projectId, onRefreshReports]);

  // Sort artifacts by creation date
  const sortedArtifacts = useMemo(() => {
    return [...artifacts].sort((a, b) => {
      const dateA = new Date(a.created_at || 0).getTime();
      const dateB = new Date(b.created_at || 0).getTime();
      return artifactSortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
  }, [artifacts, artifactSortOrder]);

  // Memoized artifacts list for better performance - prevents unnecessary re-renders
  const memoizedArtifactsList = useMemo(() => sortedArtifacts, [sortedArtifacts]);
  
  // Memoized artifacts empty state
  const artifactsEmptyState = useMemo(() => (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      py: 4,
      color: '#999999'
    }}>
      <Typography variant="body2" sx={{ color: '#999999', textAlign: 'center' }}>
        No artifacts added yet
      </Typography>
    </Box>
  ), []);

  // Sort findings by creation date
  const sortedFindings = useMemo(() => {
    return [...findings].sort((a, b) => {
      const dateA = new Date(a.created_at || 0).getTime();
      const dateB = new Date(b.created_at || 0).getTime();
      return findingSortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
  }, [findings, findingSortOrder]);

  // Memoized findings list for better performance - prevents unnecessary re-renders  
  const memoizedFindingsList = useMemo(() => sortedFindings, [sortedFindings]);
  
  // Memoized findings empty state
  const findingsEmptyState = useMemo(() => (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      py: 4,
      color: '#999999'
    }}>
      <Typography variant="body2" sx={{ color: '#999999', textAlign: 'center' }}>
        No findings added yet
      </Typography>
    </Box>
  ), []);

  // State to track which reports are still being generated (based on content check)
  const [generatingReportIds, setGeneratingReportIds] = useState<Set<string>>(new Set());
  // State to track reports that we've validated as actually ready (content appears complete)
  const [validatedReadyReportIds, setValidatedReadyReportIds] = useState<Set<string>>(new Set());
  // State to track reports that have failed (old with placeholder content or explicit errors)
  const [failedReportIds, setFailedReportIds] = useState<Set<string>>(new Set());
  const [contentCheckTrigger, setContentCheckTrigger] = useState<number>(0);

  // Initial cleanup on mount - clear any stale generating report IDs
  useEffect(() => {
    console.log('RightPanel: Initial mount cleanup - clearing generating and failed report IDs');
    setGeneratingReportIds(new Set());
    setFailedReportIds(new Set());
  }, []);

  // Function to check if report content indicates it's still being generated
  const checkReportContentStatus = useCallback(async (reportId: string): Promise<boolean> => {
    try {
      console.log('Checking content status for report:', reportId);
      const content = await reportService.getReportContentById(reportId);
      
      // DEBUG: Log the first 500 characters of content to see what we're getting
      const preview = content ? content.substring(0, 500).replace(/\n/g, '\\n') : 'NULL';
      console.log(`Report ${reportId} content preview (${content?.length || 0} chars):`, preview);
      
      // Also log the report object to see its name and description
      const report = reportsData.find(r => r.id === reportId);
      if (report) {
        console.log(`Report ${reportId} name: "${report.name}", description: "${report.description}"`);
      }
      
      if (!content || content.trim().length === 0) {
        console.log('Report content is empty, marking as generating');
        setGeneratingReportIds(prev => {
          const newSet = new Set(prev);
          newSet.add(reportId);
          return newSet;
        });
        // Ensure it's not marked validated-ready while generating
        setValidatedReadyReportIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(reportId);
          return newSet;
        });
        // IMPORTANT: Also remove from failed set - this report is actively generating
        setFailedReportIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(reportId);
          return newSet;
        });
        return true;
      }
      
      const contentLower = content.toLowerCase();
      
      // First check for ACTUAL errors in content (not just placeholder text)
      const hasActualError = contentLower.includes('error generating report') ||
                            contentLower.includes('unrecognizedclientexception') ||
                            contentLower.includes('security token included in the request is invalid') ||
                            contentLower.includes('an error occurred') ||
                            contentLower.includes('traceback') ||
                            contentLower.includes('exception:');
      
      if (hasActualError) {
        console.log(`Report ${reportId} contains actual error content - marking as failed`);
        setGeneratingReportIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(reportId);
          return newSet;
        });
        setValidatedReadyReportIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(reportId);
          return newSet;
        });
        setFailedReportIds(prev => {
          const newSet = new Set(prev);
          newSet.add(reportId);
          return newSet;
        });
        return false; // Not generating, it failed
      }
      
      // Check if content explicitly indicates report is being generated (placeholder text)
      // Be very specific to avoid false positives with actual report content
      const isPlaceholder = (
        content.trim() === 'Report is being generated...' ||
        content.trim().length < 50 ||
        (contentLower.includes('report is being generated') && content.trim().length < 200) ||
        (contentLower.includes('please wait') && contentLower.includes('generating') && content.trim().length < 300) ||
        (contentLower.includes('generation in progress') && content.trim().length < 200) ||
        (contentLower.startsWith('generating') && content.trim().length < 100)
      );
      
      if (isPlaceholder) {
        console.log('Report content indicates still generating (placeholder text)');
        setGeneratingReportIds(prev => {
          const newSet = new Set(prev);
          newSet.add(reportId);
          return newSet;
        });
        // Ensure it's not marked validated-ready while generating
        setValidatedReadyReportIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(reportId);
          return newSet;
        });
        // Clear from failed set - this report is actively generating
        setFailedReportIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(reportId);
          return newSet;
        });
        return true;
      }
      
      // Content looks complete - explicitly remove from generating set and mark validated ready
      console.log('Report content appears complete, removing from generating set and marking validated ready');
      setGeneratingReportIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(reportId);
        return newSet;
      });
      setValidatedReadyReportIds(prev => {
        const newSet = new Set(prev);
        newSet.add(reportId);
        return newSet;
      });
      // Also clear from failed set
      setFailedReportIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(reportId);
        return newSet;
      });
      return false;
    } catch (err) {
      console.error('Error checking report content for', reportId, ':', err);
      // If we can't fetch the content, remove any generating/validated markers to avoid stale UI
      console.log('Removing report from generating set due to fetch error');
      setGeneratingReportIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(reportId);
        return newSet;
      });
      setValidatedReadyReportIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(reportId);
        return newSet;
      });
      return false;
    }
  }, [reportsData]);

  // Helper function to check if a report has failed
  const isReportFailed = useCallback((report: Finding) => {
    // First check if it's in the failed set (already validated as failed)
    if (failedReportIds.has(report.id)) {
      return true;
    }
    
    // If it's actively generating, it's NOT failed
    if (generatingReportIds.has(report.id)) {
      return false;
    }
    
    // Check if processing status indicates failure (actual backend error)
    const processingStatus = (report as any).processing_status;
    if (processingStatus === 'error' || processingStatus === 'failed') {
      return true;
    }
    
    // Check if there's explicit error content from backend
    if ((report as any).error_content || (report as any).error) {
      return true;
    }
    
    // Don't mark as failed based on age or placeholder text anymore
    // Reports are either generating or ready - no failed state for timeout
    
    return false;
  }, [failedReportIds, generatingReportIds]);

  // Content validation for reports - REMOVED automatic validation to prevent infinite loops
  // Validation now happens on-demand when user tries to view/download a report
  
  // Helper function to check if a report is still being generated
  const isReportBeingGenerated = useCallback((report: Finding) => {
    // Check the processing_status field from backend
    const status = (report as any).processing_status;
    return status === 'generating';
  }, []);

  // Sort reports by creation date
  const sortedReports = useMemo(() => {
    const sorted = [...reportsData].sort((a, b) => {
      const dateA = new Date(a.created_at || 0).getTime();
      const dateB = new Date(b.created_at || 0).getTime();
      return reportSortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
    
    // Removed debug logging - was causing infinite re-renders
    // The logging was calling isReportBeingGenerated() and isReportFailed() which are callbacks
    // Don't need to log on every sort anyway
    
    return sorted;
  }, [reportsData, reportSortOrder]);

  // Handle resize start
  const handleResizeStart = (e: React.MouseEvent) => {
    setIsResizing(true);
    setStartX(e.clientX);
    setStartWidth(width);
    
    // Prevent text selection during resize
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';
  };
  
  // Handle resize during mouse move
  const handleResizeMove = React.useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    
    const deltaX = startX - e.clientX; // Subtract because we're resizing from the left
    const newWidth = Math.min(Math.max(startWidth + deltaX, 250), 600); // Min 250px, Max 600px
    
    if (onWidthChange) {
      onWidthChange(newWidth);
    }
  }, [isResizing, startX, startWidth, onWidthChange]);
  
  // Handle resize end
  const handleResizeEnd = React.useCallback(() => {
    setIsResizing(false);
    
    // Restore normal cursor and text selection
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }, []);
  
  // Add global mouse event listeners for resizing
  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isResizing, handleResizeMove, handleResizeEnd]);
  
  // Handle tab change - memoized for performance
  const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    
    // If switching to the findings tab, call the onFindingsTabClick callback
    if (newValue === 1 && onFindingsTabClick) {
      onFindingsTabClick();
    }
    
    // If switching to the artifacts tab, call the onArtifactsTabClick callback
    if (newValue === 0 && onArtifactsTabClick) {
      onArtifactsTabClick();
    }
  }, [onFindingsTabClick, onArtifactsTabClick]);

  // Artifact dialog handlers - memoized for performance
  const handleOpenArtifactDialog = useCallback(() => setArtifactDialogOpen(true), []);
  const handleCloseArtifactDialog = () => {
    setArtifactDialogOpen(false);
    artifactFormValidation.resetAll();
    setArtifactType('scope');
  };

  // Finding dialog handlers - memoized for performance
  const handleOpenFindingDialog = useCallback(() => setFindingDialogOpen(true), []);
  const handleCloseFindingDialog = () => {
    setFindingDialogOpen(false);
    findingFormValidation.resetAll();
    setFindingSeverity('medium');
  };

  // Add artifact handler
  const handleAddArtifact = () => {
    if (!artifactFormValidation.validateAll()) {
      setError('Please fix validation errors before submitting.');
      return;
    }

    const sanitizedValues = artifactFormValidation.getSanitizedValues();
    if (sanitizedValues.artifactName.trim()) {
      setArtifactSubmitting(true);
      onAddArtifact(sanitizedValues.artifactName.trim(), artifactType);
      handleCloseArtifactDialog();
      setArtifactSubmitting(false);
    }
  };

  // Add finding handler
  const handleAddFinding = () => {
    if (!findingFormValidation.validateAll()) {
      setError('Please fix validation errors before submitting.');
      return;
    }

    const sanitizedValues = findingFormValidation.getSanitizedValues();
    if (sanitizedValues.findingName.trim()) {
      setFindingSubmitting(true);
      onAddFinding(sanitizedValues.findingName.trim());
      handleCloseFindingDialog();
      setFindingSubmitting(false);
    }
  };
  
  // Handle artifact type change
  const handleArtifactTypeChange = (event: SelectChangeEvent<string>) => {
    setArtifactType(event.target.value);
  };
  
  // Handle finding severity change
  const handleSeverityChange = (event: SelectChangeEvent<'low' | 'medium' | 'high' | 'critical'>) => {
    setFindingSeverity(event.target.value as 'low' | 'medium' | 'high' | 'critical');
  };
  
  // Handle error message close
  const handleCloseError = () => {
    setError(null);
  };

  // Sort toggle functions
  const toggleArtifactSort = () => {
    setArtifactSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  const toggleFindingSort = () => {
    setFindingSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  const toggleReportSort = () => {
    setReportSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
  };
  
  // Handle delete artifact
  const handleDeleteArtifact = async (id: string) => {
    try {
      await artifactService.deleteArtifact(id);
      // Note: The parent component should refresh the artifacts list
    } catch (err) {
      console.error('Failed to delete artifact:', err);
      setError('Failed to delete artifact. Please try again.');
    }
  };
  
  // Handle delete finding
  const handleDeleteFinding = async (id: string) => {
    try {
      await artifactService.deleteFinding(id);
      // Note: The parent component should refresh the findings list
    } catch (err) {
      console.error('Failed to delete finding:', err);
      setError('Failed to delete finding. Please try again.');
    }
  };

  const [uploadingFile, setUploadingFile] = useState<boolean>(false);

  // Handle file upload click
  const handleUploadClick = (id: string, type: 'artifact' | 'finding') => {
    setFileUploadTarget({ id, type });
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle file selection
  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !fileUploadTarget) {
      return;
    }

    const file = files[0];

    // Validate the file
    const validationResult = validateFile(file);
    
    if (!validationResult.valid) {
      // Show error message
      setError(validationResult.error || 'Invalid file');
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const targetId = fileUploadTarget.id;
    
    console.log('Starting file upload for', fileUploadTarget.type, targetId, 'with file:', file.name);
    
    // Set uploading state for specific artifact
    setUploadingArtifacts(prev => ({ ...prev, [targetId]: true }));
    setError(null);

    try {
      let response;
      if (fileUploadTarget.type === 'artifact') {
        console.log('Uploading artifact file...');
        response = await artifactService.uploadArtifactFile(targetId, file, projectId);
        console.log('Artifact upload response:', response);
        
        // If the file is being processed, check for processing status
        if (response && response.processing_status === 'pending') {
          console.log('File uploaded and being analyzed:', response);
        }
      } else {
        console.log('Uploading finding file...');
        response = await artifactService.uploadFindingFile(targetId, file, projectId);
        console.log('Finding upload response:', response);
      }

      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      console.log('File upload completed successfully');
      
      // Trigger refresh to update the UI immediately
      if (fileUploadTarget.type === 'artifact' && onRefreshArtifacts) {
        console.log('Refreshing artifacts after upload');
        onRefreshArtifacts();
      } else if (fileUploadTarget.type === 'finding' && onRefreshFindings) {
        console.log('Refreshing findings after upload');
        onRefreshFindings();
      }

    } catch (err: any) {
      console.error('File upload error details:', err);
      console.error('Error type:', typeof err);
      console.error('Error keys:', Object.keys(err || {}));
      
      // Check if this is actually an HTTP error
      if (err?.response) {
        console.error('HTTP error response:', err.response.status, err.response.data);
        const errorMessage = err.response.data?.detail || err.response.data?.message || `Upload failed with status ${err.response.status}`;
        setError(errorMessage);
      } else if (err?.message) {
        console.error('Error message:', err.message);
        setError(err.message);
      } else {
        console.error('Unknown error type, treating as success');
        // If it's not clearly an error, don't show error message
        // Sometimes the upload succeeds but throws due to response parsing issues
      }
    } finally {
      // Clear uploading state for specific artifact
      setUploadingArtifacts(prev => ({ ...prev, [targetId]: false }));
      setFileUploadTarget(null);
    }
  };

  // Handle file view
  const handleViewFile = (item: Artifact | Finding, type: 'artifact' | 'finding') => {
    if (!item.has_file || !item.file_name || !item.file_type) {
      setError('No file attached to this item.');
      return;
    }

    const fileUrl = type === 'artifact' 
      ? artifactService.getArtifactFileUrl(item.id)
      : artifactService.getFindingFileUrl(item.id);

    setFileViewerProps({
      fileUrl,
      fileName: item.file_name,
      fileType: item.file_type
    });
    setFileViewerOpen(true);
  };

  // Handle close file viewer
  const handleCloseFileViewer = () => {
    setFileViewerOpen(false);
  };
  
  // Report generation state
  const [reportGenerating, setReportGenerating] = useState<boolean>(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportContent, setReportContent] = useState<string>('');
  const [loadingReport, setLoadingReport] = useState<boolean>(false);
  const [selectedReport, setSelectedReport] = useState<Finding | null>(null);
  
  // Function to fetch a specific report's content
  const fetchReportContent = async (reportId: string) => {
    if (!reportId) return;
    
    setLoadingReport(true);
    setReportError(null);
    
    try {
      console.log('Fetching content for report ID:', reportId);
      // Get the content of the specified report
      const content = await reportService.getReportContentById(reportId);
      if (content) {
        setReportContent(content);
      } else {
        setReportError('Could not load report content');
      }
    } catch (err) {
      console.error('Error fetching report content:', err);
      setReportError('Error loading report content');
    } finally {
      setLoadingReport(false);
    }
  };
  
  // Automatic content validation REMOVED - was causing infinite re-render loop
  // The useEffect was modifying generatingReportIds which was also a dependency
  // Validation now happens on-demand only when user tries to view/download a report
  // Reports will update when user manually clicks Refresh or tries to view/download
  
  // Handle generate report
  const handleGenerateReport = async () => {
    if (!projectId) {
      setError('No project selected. Please select a project first.');
      return;
    }
    
    setReportGenerating(true);
    setReportError(null);
    setReportContent('');
    setSelectedReport(null);
    
    try {
      if (onGenerateReport) {
        // Use the parent component's generate report function to maintain chat context
        const success = await onGenerateReport(projectId);
        if (!success) {
          throw new Error('Failed to generate report');
        }
      } else {
        // Direct report generation if no parent handler is provided
        await reportService.generateReport(projectId, "Penetration Test Report", true, true);
      }
      
      console.log('Report generation started');
      
      // Show a success message but keep the generating state for a bit
      setTimeout(() => {
        setReportGenerating(false);
        // Trigger immediate refresh through parent component
        if (onRefreshReports) {
          console.log('Triggering immediate reports refresh after generation');
          onRefreshReports();
        }
      }, 3000); // Show generating state for at least 3 seconds
    } catch (err) {
      console.error('Failed to generate report:', err);
      setReportError('Failed to start report generation. Please try again.');
      setReportGenerating(false);
    }
  };

  // Handle regenerate/revalidate failed report
  const handleRegenerateReport = async (reportId: string) => {
    console.log(`Regenerating/revalidating report ${reportId}`);
    
    // Clear the failed status for this report
    setFailedReportIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(reportId);
      return newSet;
    });
    
    // Clear from generating set too
    setGeneratingReportIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(reportId);
      return newSet;
    });
    
    // Clear from validated set to force re-check
    setValidatedReadyReportIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(reportId);
      return newSet;
    });
    
    // Trigger a refresh to get latest data
    if (onRefreshReports) {
      await onRefreshReports();
    }
    
    // Force content check after a short delay
    setTimeout(async () => {
      console.log(`Forcing content check for report ${reportId}`);
      try {
        const isStillGenerating = await checkReportContentStatus(reportId);
        if (!isStillGenerating) {
          console.log(`Report ${reportId} is now ready after regeneration check`);
        } else {
          console.log(`Report ${reportId} is still generating or has placeholder content`);
        }
      } catch (err) {
        console.error(`Error during regeneration check for ${reportId}:`, err);
      }
    }, 500);
  };
  
  // Handle report refresh
  const handleRefreshReports = () => {
    console.log('Refreshing reports...');
    // Call parent refresh callback to trigger MainLayout fetchReports
    if (onRefreshReports) {
      onRefreshReports();
      // Force content check after manual refresh
      setTimeout(() => {
        setContentCheckTrigger(prev => prev + 1);
      }, 500); // Small delay to let the data load
    }
  };
  
  // Handle view report
  const handleViewReport = async (report: Finding) => {
    setSelectedReport(report);

    // If we haven't validated this report as ready yet, do a content check.
    const isValidated = validatedReadyReportIds.has(report.id);
    if (!isValidated) {
      const isGenerating = await checkReportContentStatus(report.id);
      if (isGenerating) {
        // Don't show a banner; simply don't open the viewer until content is ready.
        console.log(`User attempted to view report ${report.id} while it is still generating`);
        return;
      }
    }

    // If report appears failed, don't open viewer (no banner)
    if (isReportFailed(report)) {
      console.log(`Report ${report.id} appears failed, not opening viewer`);
      return;
    }

    if (report && report.has_file) {
      console.log('Viewing report:', report);
      // For reports that are Finding objects but with type="report",
      // we use the finding file URL, otherwise we need to check if it's a new-style report
      let fileUrl = '';
      
      // If this appears to be a mongo ObjectId (new-style report),
      // use the report content URL instead of finding URL
      if (report.id.length === 24 && /^[0-9a-f]{24}$/i.test(report.id)) {
        console.log('Using report content URL for report:', report.id);
        fileUrl = reportService.getReportFileUrl(report.id);
      } else {
        console.log('Using finding file URL for report:', report.id);
        fileUrl = artifactService.getFindingFileUrl(report.id);
      }
      
      // Set up file viewer properties for the report
      setFileViewerProps({
        fileUrl,
        fileName: report.file_name || 'report.md',
        fileType: report.file_type || 'markdown'
      });
      setFileViewerOpen(true);
    } else {
      console.log('Selected report has no file attached or is not ready for viewing.');
    }
  };
  
  // Handle report download
  // Handler to open download menu
  const handleDownloadMenuOpen = (event: React.MouseEvent<HTMLElement>, reportId: string) => {
    setDownloadMenuAnchor(event.currentTarget);
    setSelectedReportForDownload(reportId);
  };

  // Handler to close download menu
  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
    setSelectedReportForDownload(null);
  };

  const handleDownloadReport = async (reportId: string, format: 'markdown' | 'word' = 'markdown') => {
    try {
      // Gate downloads: prefer the validated-ready set. If not validated, try a content check and quietly abort if still generating.
      const validated = validatedReadyReportIds.has(reportId);
      if (!validated) {
        const isGenerating = await checkReportContentStatus(reportId);
        if (isGenerating) {
          console.log(`User attempted to download report ${reportId} while it is still generating`);
          return;
        }
      }

      // Find the report in our data to check its status; if failed, quietly abort
      const report = reportsData.find(r => r.id === reportId);
      if (report && isReportFailed(report)) {
        console.log(`Report ${reportId} appears failed; aborting download`);
        return;
      }

      
      // Use the new download method with format parameter (this handles the download properly)
      if (format === 'word') {
        await reportService.downloadReportAsWord(reportId);
      } else {
        await reportService.downloadReportAsMarkdown(reportId);
      }
    } catch (err) {
      console.error(`Failed to download report in ${format} format:`, err);
      setError(`Failed to download report in ${format} format. Please try again or wait if the report is still being generated.`);
    }
  };

  return (
    <PanelContainer width={width}>
      <ResizeHandle onMouseDown={handleResizeStart}>
        <DragHandleIcon fontSize="small" />
      </ResizeHandle>
      
      {/* Tab navigation */}
      <div style={{backgroundColor: '#ffffff',padding: '15px' , borderBottom: '1px solid #E5E7EB'}}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="fullWidth"
          aria-label="panel sections"
          sx={{
            '& .MuiTabs-indicator': {
              display: 'none',
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              overflow: 'visible',
              position: 'relative',
              marginTop: '15px',
            },
          }}
        >
          <Tab
            style={{ backgroundColor: currentTab === 0 ? '#F3E8FF' : 'transparent', color: '#000000ff', borderRadius: '20px', fontWeight: 'bold', position: 'relative' }}
            label={
              <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                <span className='word-inline'>Artifacts</span>
                {artifacts.length > 0 && (
                  <Badge 
                    badgeContent={artifacts.length} 
                    color="error"
                    sx={{
                      position: 'absolute',
                      top: '-10px',
                      right: artifacts.length >= 10 ? '4px' : '8px', // Adjust position for 2-digit numbers
                      '& .MuiBadge-badge': {
                        backgroundColor: '#dc3545',
                        color: 'white',
                        fontSize: '0.65rem',
                        minWidth: artifacts.length >= 10 ? '20px' : '16px', // Wider for 2-digit numbers
                        height: '16px',
                        borderRadius: '8px',
                        padding: '0 4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }
                    }}
                  >
                    <span style={{ visibility: 'hidden', width: '0px', height: '0px' }}></span>
                  </Badge>
                )}
              </Box>
            }
            id="tab-0" 
            aria-controls="tabpanel-0"
          />
          <Tab
            style={{ backgroundColor: currentTab === 1 ? '#F3E8FF' : 'transparent', color: '#000000ff', borderRadius: '20px', fontWeight: 'bold', position: 'relative' }}
            label={
              <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                <span className="word-inline">Analysis</span>
                {findings.length > 0 && (
                  <Badge 
                    badgeContent={findings.length} 
                    color="error"
                    sx={{
                      position: 'absolute',
                      top: '-10px',
                      right: findings.length >= 10 ? '4px' : '8px', // Adjust position for 2-digit numbers
                      '& .MuiBadge-badge': {
                        backgroundColor: '#dc3545',
                        color: 'white',
                        fontSize: '0.65rem',
                        minWidth: findings.length >= 10 ? '20px' : '16px', // Wider for 2-digit numbers
                        height: '16px',
                        borderRadius: '8px',
                        padding: '0 4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }
                    }}
                  >
                    <span style={{ visibility: 'hidden', width: '0px', height: '0px' }}></span>
                  </Badge>
                )}
              </Box>
            }
            id="tab-1" 
            aria-controls="tabpanel-1"
          />
          <Tab
            style={{ backgroundColor: currentTab === 2 ? '#F3E8FF' : 'transparent', color: '#000000ff', borderRadius: '20px', fontWeight: 'bold', position: 'relative' }}
            label={
              <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                <span className='word-inline'>Reports</span>
                {reportsData.length > 0 && (
                  <Badge 
                    badgeContent={reportsData.length} 
                    color="error"
                    sx={{
                      position: 'absolute',
                      top: '-10px',
                      right: reportsData.length >= 10 ? '4px' : '8px', // Adjust position for 2-digit numbers
                      '& .MuiBadge-badge': {
                        backgroundColor: '#dc3545',
                        color: 'white',
                        fontSize: '0.65rem',
                        minWidth: reportsData.length >= 10 ? '20px' : '16px', // Wider for 2-digit numbers
                        height: '16px',
                        borderRadius: '8px',
                        padding: '0 4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }
                    }}
                  >
                    <span style={{ visibility: 'hidden', width: '0px', height: '0px' }}></span>
                  </Badge>
                )}
              </Box>
            }
            id="tab-2" 
            aria-controls="tabpanel-2" 
          />
        </Tabs>
      </div>
      
      {/* Artifacts Section */}
      <SectionContainer sx={{ display: currentTab === 0 ? 'flex' : 'none' }}>
        <SectionHeader>
          <AddButton
            startIcon={<AddIcon />}
            onClick={handleOpenArtifactDialog}
          >
            Add Artifact
          </AddButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={`Sort ${artifactSortOrder === 'desc' ? 'Ascending' : 'Descending'}`}>
              <IconButton 
                size="small" 
                onClick={toggleArtifactSort}
                sx={{ 
                  color: '#666666',
                  '&:hover': {
                    backgroundColor: 'rgba(17, 202, 240, 0.1)',
                    color: '#11CAF0'
                  }
                }}
              >
                <SortIcon 
                  fontSize="small" 
                  sx={{ 
                    transform: artifactSortOrder === 'asc' ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.3s ease'
                  }} 
                />
              </IconButton>
            </Tooltip>
            {onRefreshArtifacts && (
              <Tooltip title="Refresh Artifacts">
                <IconButton 
                  size="small" 
                  onClick={() => onRefreshArtifacts()}
                  sx={{ 
                    color: '#666666',
                    '&:hover': {
                      backgroundColor: 'rgba(17, 202, 240, 0.1)',
                      color: '#11CAF0'
                    }
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </SectionHeader>
        
        <ItemList>
          {memoizedArtifactsList.length === 0 ? (
            artifactsEmptyState
          ) : (
            memoizedArtifactsList.map((artifact) => (
              <ListItem
                key={artifact.id}
                sx={{ 
                  flexDirection: 'column', 
                  alignItems: 'stretch',
                  py: 2,
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  mb: 1,
                  maxWidth: '100%',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ width: '100%', mb: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 600, 
                        color: '#333333', 
                        mr: 1,
                        wordBreak: 'break-word',
                        minWidth: 0,
                        flex: '1 1 auto'
                      }}
                    >
                      {artifact.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      
                    </Box>
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#666666', 
                      lineHeight: 1.4,
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word'
                    }}
                  >
                    {artifact.type && (
                      <span style={{ fontWeight: 'bold' }}>{artifact.type}: </span>
                    )}
                    {artifact.processing_summary && typeof artifact.processing_summary === 'string' ? (
                      <span>{artifact.processing_summary.substring(0, 100)}{artifact.processing_summary.length > 100 ? '...' : ''}</span>
                    ) : (
                      <span>No summary available</span>
                    )}
                  </Typography>
                  {artifact.processing_status && (
                    <Typography 
                      variant="caption" 
                      component="div" 
                      sx={{ 
                        mt: 0.5, 
                        color: artifact.processing_status === 'success' ? '#4caf50' : 
                               artifact.processing_status === 'processing' ? '#2196f3' : 
                               artifact.processing_status === 'error' ? '#f44336' : '#666666' 
                      }}
                    >
                      Status: {artifact.processing_status === 'success' ? 'Processed' : 
                               artifact.processing_status === 'processing' ? 'Processing...' : 
                               artifact.processing_status === 'error' ? 'Error' : 'Pending'}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  gap: 1,
                  width: '100%',
                  flexShrink: 0
                }}>
                  {/* For manual artifacts without files, show upload icon */}
                  {artifact.is_manual ? (
                    !artifact.has_file ? (
                      <Tooltip title={`Upload file (${getAllowedFileTypesDisplay()})`}>
                        <IconButton
                          size="small"
                          onClick={() => handleUploadClick(artifact.id, 'artifact')}
                          disabled={uploadingArtifacts[artifact.id]}
                          sx={{ color: '#666666' }}
                        >
                          {uploadingArtifacts[artifact.id] ? (
                            <CircularProgress 
                              size={20} 
                              sx={{ 
                                color: '#1976d2',
                                animationDuration: '1.4s',
                                '& .MuiCircularProgress-circle': {
                                  strokeLinecap: 'round',
                                }
                              }} 
                            />
                          ) : (
                            <UploadFileIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="View file">
                        <IconButton
                          size="small"
                          onClick={() => handleViewFile(artifact, 'artifact')}
                          sx={{ color: '#666666' }}
                        >
                          <FileOpenIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )
                  ) : (
                    <Tooltip title={artifact.has_file ? "View file" : "No file available"}>
                      <IconButton
                        size="small"
                        onClick={() => artifact.has_file ? handleViewFile(artifact, 'artifact') : null}
                        sx={{ 
                          color: artifact.has_file ? '#666666' : '#9e9e9e'
                        }}
                      >
                        <FileOpenIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Delete">
                    <IconButton 
                      size="small"
                      onClick={() => handleDeleteArtifact(artifact.id)}
                      sx={{ color: '#666666' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItem>
              
            ))
          )}
        </ItemList>
      </SectionContainer>
      
      
      {/* Findings Section */}
      <SectionContainer sx={{ display: currentTab === 1 ? 'flex' : 'none' }}>
        <SectionHeader>
          <AddButton
            startIcon={<AddIcon />}
            onClick={handleOpenFindingDialog}
          >
            Add Analysis
          </AddButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={`Sort ${findingSortOrder === 'desc' ? 'Ascending' : 'Descending'}`}>
              <IconButton 
                size="small" 
                onClick={toggleFindingSort}
                sx={{ 
                  color: '#666666',
                  '&:hover': {
                    backgroundColor: 'rgba(17, 202, 240, 0.1)',
                    color: '#11CAF0'
                  }
                }}
              >
                <SortIcon 
                  fontSize="small" 
                  sx={{ 
                    transform: findingSortOrder === 'asc' ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.3s ease'
                  }} 
                />
              </IconButton>
            </Tooltip>
            {onRefreshFindings && (
              <Tooltip title="Refresh Analysis">
                <IconButton 
                  size="small" 
                  onClick={() => onRefreshFindings()}
                  sx={{ 
                    color: '#666666',
                    '&:hover': {
                      backgroundColor: 'rgba(17, 202, 240, 0.1)',
                      color: '#11CAF0'
                    }
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </SectionHeader>
        
        <ItemList>
          {memoizedFindingsList.length === 0 ? (
            findingsEmptyState
          ) : (
            memoizedFindingsList.map((finding) => (
              <ListItem
                key={finding.id}
                sx={{ 
                  flexDirection: 'column', 
                  alignItems: 'stretch',
                  py: 2,
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  mb: 1,
                  maxWidth: '100%',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ width: '100%', mb: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 600, 
                        color: '#333333', 
                        mr: 1,
                        wordBreak: 'break-word',
                        minWidth: 0,
                        flex: '1 1 auto'
                      }}
                    >
                      {finding.name}
                    </Typography>
                    
                  </Box>
                  {finding.description && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#666666', 
                        lineHeight: 1.4,
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word'
                      }}
                    >
                      {finding.description.length > 100 
                        ? `${finding.description.substring(0, 100)}...` 
                        : finding.description}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  gap: 1,
                  width: '100%',
                  flexShrink: 0
                }}>
                  {finding.is_manual ? (
                    !finding.has_file ? (
                      <Tooltip title={`Upload file (${getAllowedFileTypesDisplay()})`}>
                        <IconButton
                          size="small"
                          onClick={() => handleUploadClick(finding.id, 'finding')}
                          disabled={uploadingArtifacts[finding.id]}
                          sx={{ color: '#666666' }}
                        >
                          {uploadingArtifacts[finding.id] ? (
                            <CircularProgress 
                              size={20} 
                              sx={{ 
                                color: '#1976d2',
                                animationDuration: '1.4s',
                                '& .MuiCircularProgress-circle': {
                                  strokeLinecap: 'round',
                                }
                              }} 
                            />
                          ) : (
                            <UploadFileIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="View file">
                        <IconButton
                          size="small"
                          onClick={() => handleViewFile(finding, 'finding')}
                          sx={{ color: '#666666' }}
                        >
                          <FileOpenIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )
                  ) : (
                    <Tooltip title={finding.has_file ? "View file" : "No file available"}>
                      <IconButton
                        size="small"
                        onClick={() => finding.has_file ? handleViewFile(finding, 'finding') : null}
                        sx={{ 
                          color: finding.has_file ? '#666666' : '#9e9e9e'
                        }}
                      >
                        <FileOpenIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Delete">
                    <IconButton 
                      size="small"
                      onClick={() => handleDeleteFinding(finding.id)}
                      sx={{ color: '#666666' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItem>
            ))
          )}
        </ItemList>
      </SectionContainer>

      {/* Report Section */}
      <SectionContainer sx={{ display: currentTab === 2 ? 'flex' : 'none' }}>
        <SectionHeader>
          <Button
            size="small"
            color="primary"
            variant="contained"
            onClick={handleGenerateReport}
            disabled={reportGenerating}
            startIcon={reportGenerating ? <CircularProgress 
              size={20} 
              sx={{ 
                color: 'white',
                animationDuration: '1.4s',
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                }
              }} 
            /> : null}
          >
            {reportGenerating ? 'Generating...' : 'Generate Report'}
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={`Sort ${reportSortOrder === 'desc' ? 'Ascending' : 'Descending'}`}>
              <IconButton 
                size="small" 
                onClick={toggleReportSort}
                sx={{ 
                  color: '#666666',
                  '&:hover': {
                    backgroundColor: 'rgba(17, 202, 240, 0.1)',
                    color: '#11CAF0'
                  }
                }}
              >
                <SortIcon 
                  fontSize="small" 
                  sx={{ 
                    transform: reportSortOrder === 'asc' ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.3s ease'
                  }} 
                />
              </IconButton>
            </Tooltip>
            {onRefreshReports && (
              <Tooltip title="Refresh Reports">
                <IconButton 
                  size="small" 
                  onClick={() => onRefreshReports()}
                  sx={{ 
                    color: '#666666',
                    '&:hover': {
                      backgroundColor: 'rgba(17, 202, 240, 0.1)',
                      color: '#11CAF0'
                    }
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </SectionHeader>
        
        {reportError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {reportError}
          </Alert>
        )}
        
        {reportGenerating ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3, flex: 1 }}>
            <CircularProgress 
              sx={{ 
                mb: 3,
                color: '#1976d2',
                animationDuration: '1.4s',
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                }
              }} 
            />
            <Typography variant="body1" color="black" sx={{ mb: 1, textAlign: 'center', fontWeight: 'medium' }}>
              Generating comprehensive report...
            </Typography>
            <Typography variant="body2" color="black" sx={{ textAlign: 'center' }}>
              This may take a few minutes. Please wait while we analyze your project data.
            </Typography>
          </Box>
        ) : loadingReport ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3, flex: 1 }}>
            <CircularProgress 
              sx={{ 
                mb: 3,
                color: '#1976d2',
                animationDuration: '1.4s',
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                }
              }} 
              size={30} 
            />
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              Loading report...
            </Typography>
          </Box>
        ) : reportsData.length > 0 ? (
          <>
            <ItemList>
              {sortedReports.map((report) => (
                <ListItem
                  key={report.id}
                  sx={{ 
                    flexDirection: 'column', 
                    alignItems: 'stretch',
                    py: 2,
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    mb: 1,
                    maxWidth: '100%',
                    overflow: 'hidden'
                  }}
                >
                  <Box sx={{ width: '100%', mb: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          fontWeight: 600, 
                          color: '#333333', 
                          mr: 1,
                          wordBreak: 'break-word',
                          minWidth: 0,
                          flex: '1 1 auto'
                        }}
                      >
                        {report.name || "Project Report"}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        
                      </Box>
                    </Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#666666', 
                        lineHeight: 1.4,
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word'
                      }}
                    >
                      Generated: {new Date(report.created_at).toLocaleString()}
                    </Typography>
                    {/* Always show status - determine based on report state */}
                    {(() => {
                      const processingStatus = (report as any).processing_status;
                      const isGenerating = processingStatus === 'generating';
                      const isCompleted = processingStatus === 'completed';
                      const hasError = processingStatus === 'error';

                      // Show status based on backend processing_status field
                      if (hasError) {
                        return (
                          <Typography 
                            variant="caption" 
                            component="div" 
                            sx={{ 
                              mt: 0.5, 
                              color: '#f44336',
                              fontWeight: 'bold'
                            }}
                          >
                            Status: Error
                          </Typography>
                        );
                      }

                      if (isCompleted) {
                        return (
                          <Typography 
                            variant="caption" 
                            component="div" 
                            sx={{ 
                              mt: 0.5, 
                              color: '#4caf50',
                              fontWeight: 'bold'
                            }}
                          >
                            Status: Ready to View
                          </Typography>
                        );
                      }

                      // Otherwise, show Generating
                      return (
                        <Typography 
                          variant="caption" 
                          component="div" 
                          sx={{ 
                            mt: 0.5, 
                            color: '#ff9800',
                            fontWeight: 'bold'
                          }}
                        >
                          Status: Fetching Report...
                        </Typography>
                      );
                    })()}
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    gap: 1,
                    width: '100%',
                    flexShrink: 0
                  }}>
                    {((report as any).processing_status === 'completed' || (report as any).processing_status === 'error') && report.has_file ? (
                      <>
                        <Tooltip title={(report as any).processing_status === 'error' ? "View error report" : "View report"}>
                          <IconButton
                            size="small"
                            onClick={() => handleViewReport(report)}
                            sx={{ color: '#666666' }}
                          >
                            <FileOpenIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={(report as any).processing_status === 'error' ? "Download error report" : "Download report"}>
                          <IconButton 
                            size="small"
                            onClick={(e) => handleDownloadMenuOpen(e, report.id)}
                            sx={{ color: '#666666' }}
                          >
                            <GetAppIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Report is still being generated">
                          <IconButton
                            size="small"
                            disabled
                            sx={{ color: '#cccccc' }}
                          >
                            <FileOpenIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Report is not ready for download">
                          <IconButton 
                            size="small"
                            disabled
                            sx={{ color: '#cccccc' }}
                          >
                            <GetAppIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                </ListItem>
              ))}
            </ItemList>
          </>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3, flex: 1 }}>
            <DescriptionIcon sx={{ fontSize: 60, color: '#666666', mb: 2, opacity: 0.5 }} />
            <Typography variant="body1" color="#666666" sx={{ mb: 1, textAlign: 'center', fontWeight: 'medium' }}>
              No reports generated yet
            </Typography>
            <Typography variant="body2" color="#666666" sx={{ textAlign: 'center' }}>
              Click the Generate Report button to create a comprehensive report based on your project's findings and artifacts.
            </Typography>
          </Box>
        )}
      </SectionContainer>

      {/* Artifact Dialog */}
      <Dialog 
        open={artifactDialogOpen} 
        onClose={handleCloseArtifactDialog} 
        fullWidth 
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: '#FFFFFF', // White background
            color: '#000000', // Black text
          }
        }}
      >
        <DialogTitle sx={{ backgroundColor: '#FFFFFF', color: '#000000' }}>
          Add Artifact
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#FFFFFF', color: '#000000' }}>
          <TextField
            margin="dense"
            id="artifactName"
            label="Artifact Name"
            type="text"
            fullWidth
            variant="outlined"
            disabled={artifactSubmitting}
            {...artifactFormValidation.fields.artifactName.getInputProps()}
            slotProps={{ htmlInput: { maxLength: 100 } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                color: '#000000',
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#000000',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(0, 0, 0, 0.7)',
                '&.Mui-focused': {
                  color: '#000000',
                },
              },
            }}
          />
          <FormControl 
            fullWidth 
            margin="dense"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                color: '#000000',
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#000000',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(0, 0, 0, 0.7)',
                '&.Mui-focused': {
                  color: '#000000',
                },
              },
              '& .MuiSvgIcon-root': {
                color: 'rgba(0, 0, 0, 0.7)',
              },
            }}
          >
            <InputLabel id="artifact-type-label">Type</InputLabel>
            <Select
              labelId="artifact-type-label"
              id="artifact-type"
              value={artifactType}
              label="Type"
              onChange={handleArtifactTypeChange}
              disabled={artifactSubmitting}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: '#FFFFFF',
                    color: '#000000',
                    '& .MuiMenuItem-root': {
                      color: '#000000',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        },
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="scope">Project Scope</MenuItem>
              <MenuItem value="credentials">Credentials</MenuItem>
              <MenuItem value="scan_result">Scan Result</MenuItem>
              <MenuItem value="document_search">Document Search</MenuItem>
              <MenuItem value="general">General</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#FFFFFF', color: '#000000' }}>
          <Button 
            onClick={handleCloseArtifactDialog} 
            disabled={artifactSubmitting}
            sx={{ color: '#000000', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddArtifact} 
            variant="contained" 
            disabled={!artifactFormValidation.fields.artifactName.value.trim() || artifactSubmitting}
            startIcon={artifactSubmitting ? <CircularProgress size={20} sx={{ color: '#ffffff' }} /> : null}
            sx={{
              backgroundColor: '#000000',
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: '#333333',
              },
              '&:disabled': {
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                color: 'rgba(0, 0, 0, 0.5)',
              },
            }}
          >
            {artifactSubmitting ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Finding Dialog */}
      <Dialog 
        open={findingDialogOpen} 
        onClose={handleCloseFindingDialog} 
        fullWidth 
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: '#FFFFFF', // White background
            color: '#000000', // Black text
          }
        }}
      >
        <DialogTitle sx={{ backgroundColor: '#FFFFFF', color: '#000000' }}>
          Add Analysis
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#FFFFFF', color: '#000000' }}>
          <TextField
            margin="dense"
            id="findingName"
            label="Analysis Name"
            type="text"
            fullWidth
            variant="outlined"
            disabled={findingSubmitting}
            {...findingFormValidation.fields.findingName.getInputProps()}
            slotProps={{ htmlInput: { maxLength: 100 } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                color: '#000000',
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#000000',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(0, 0, 0, 0.7)',
                '&.Mui-focused': {
                  color: '#000000',
                },
              },
            }}
          />
          <TextField
            margin="dense"
            id="findingDescription"
            label="Description (optional)"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            disabled={findingSubmitting}
            {...findingFormValidation.fields.findingDescription.getInputProps()}
            slotProps={{ htmlInput: { maxLength: 500 } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                color: '#000000',
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#000000',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(0, 0, 0, 0.7)',
                '&.Mui-focused': {
                  color: '#000000',
                },
              },
            }}
          />
          <FormControl 
            fullWidth 
            margin="dense"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                color: '#000000',
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#000000',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(0, 0, 0, 0.7)',
                '&.Mui-focused': {
                  color: '#000000',
                },
              },
            }}
          >
            <InputLabel id="severity-label">Severity</InputLabel>
            <Select
              labelId="severity-label"
              id="severity"
              value={findingSeverity}
              label="Severity"
              onChange={handleSeverityChange}
              disabled={findingSubmitting}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: '#FFFFFF',
                    color: '#000000',
                    '& .MuiMenuItem-root': {
                      color: '#000000',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        },
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#FFFFFF', color: '#000000' }}>
          <Button 
            onClick={handleCloseFindingDialog} 
            disabled={findingSubmitting}
            sx={{ color: '#000000', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddFinding} 
            variant="contained" 
            disabled={!findingFormValidation.fields.findingName.value.trim() || findingSubmitting}
            startIcon={findingSubmitting ? <CircularProgress size={20} sx={{ color: '#ffffff' }} /> : null}
            sx={{
              backgroundColor: '#000000',
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: '#333333',
              },
              '&:disabled': {
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                color: 'rgba(0, 0, 0, 0.5)',
              },
            }}
          >
            {findingSubmitting ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Error Snackbar */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseError}>
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileSelected}
        accept={getAcceptAttribute()}
      />
      
      {/* File Viewer Dialog */}
      {fileViewerOpen && fileViewerProps && (
        <FileViewer
          open={fileViewerOpen}
          onClose={handleCloseFileViewer}
          fileUrl={fileViewerProps.fileUrl}
          fileName={fileViewerProps.fileName}
          fileType={fileViewerProps.fileType}
        />
      )}
      
      {/* Download Format Menu */}
      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={handleDownloadMenuClose}
      >
        <MenuItem onClick={() => selectedReportForDownload && handleDownloadReport(selectedReportForDownload, 'markdown')}>
          <DescriptionIcon fontSize="small" sx={{ mr: 1 }} />
          Download as Markdown (.md)
        </MenuItem>
        <MenuItem onClick={() => selectedReportForDownload && handleDownloadReport(selectedReportForDownload, 'word')}>
          <DescriptionIcon fontSize="small" sx={{ mr: 1 }} />
          Download as Word (.docx)
        </MenuItem>
      </Menu>
    </PanelContainer>
  );
};

export default RightPanel;